<script setup>
// Renders email HTML inside a sandboxed iframe so the dashboard styles
// (Tailwind preflight, prose) don't reflow tables, images and signatures:
// the email looks the same as in Gmail or Outlook. Scripts never run
// (no allow-scripts); allow-same-origin only lets us measure the height.
import { computed, onBeforeUnmount, useTemplateRef } from 'vue';
import { sanitize, allowedCssProperties } from 'lettersanitizer';

const props = defineProps({
  html: { type: String, default: '' },
  text: { type: String, default: '' },
});

const emit = defineEmits(['resize']);

const wrapper = useTemplateRef('wrapper');
const frame = useTemplateRef('frame');
let resizeObserver = null;
let themeObserver = null;

const srcdoc = computed(() => {
  const body = sanitize(props.html, props.text, {
    id: 'email-content',
    allowedCssProperties: [
      ...allowedCssProperties,
      'transform',
      'transform-origin',
    ],
  });
  const scheme = getComputedStyle(document.documentElement).colorScheme;
  return `<!doctype html><html style="color-scheme:${scheme}"><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0;background:transparent}body{font-size:14px;line-height:1.5;overflow-wrap:anywhere}img{max-width:100%;height:auto}a{color:rgb(39,129,246)}</style></head><body>${body}</body></html>`;
});

// The frame must use the dashboard's color-scheme: when they differ, the
// browser paints the frame opaque white, so dark mode showed white emails.
const syncTheme = doc => {
  doc.documentElement.style.colorScheme = getComputedStyle(
    document.documentElement
  ).colorScheme;
  const { color, fontFamily } = getComputedStyle(wrapper.value);
  doc.body.style.color = color;
  doc.body.style.fontFamily = fontFamily;
};

const disconnect = () => {
  resizeObserver?.disconnect();
  themeObserver?.disconnect();
};

const onLoad = () => {
  const doc = frame.value?.contentDocument;
  if (!doc?.body) return;
  disconnect();
  syncTheme(doc);
  const fit = () => {
    frame.value.style.height = `${doc.documentElement.scrollHeight}px`;
    emit('resize');
  };
  fit();
  resizeObserver = new ResizeObserver(fit);
  resizeObserver.observe(doc.body);
  themeObserver = new MutationObserver(() => syncTheme(doc));
  themeObserver.observe(document.body, { attributeFilter: ['class'] });
  themeObserver.observe(document.documentElement, {
    attributeFilter: ['style'],
  });
};

onBeforeUnmount(disconnect);
</script>

<template>
  <div ref="wrapper" class="text-n-slate-12">
    <iframe
      ref="frame"
      :srcdoc="srcdoc"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      class="block w-full border-0"
      @load="onLoad"
    />
  </div>
</template>
